import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SendIcon, CloseIcon } from './icons.jsx'
import { useAuth } from '../auth.jsx'
import { useRbac, pathAllowedFor } from '../lib/rbac.js'
import { useManifest } from '../lib/data.jsx'
import { listEvents, EVENT_TYPES, relativeTime } from '../lib/events.js'
import { gmailStatus, gmailAssistant } from '../lib/gmail.js'
import { listIntel } from '../lib/intel.js'
import { listMembers } from '../lib/team.js'
import { retrieveLeadContext, CAPABILITIES } from '../lib/workspaceContext.js'
import { CATEGORIES, categoryPath } from '../lib/categories.js'

// Workspace assistant, rendered as a full-height drawer sliding in from the
// right edge (triggered from the Topbar, next to notifications). Questions go
// to the OpenAI-backed /api/gmail/assistant endpoint, grounded in a snapshot
// of live workspace facts (manifest counts, the pipeline, the events log,
// Gmail status) so it converses freely without inventing numbers. When the
// endpoint isn't configured or reachable, the deterministic local answers
// below take over — same data, just rigid phrasing.

// The opening chips. None may name a feature the app no longer has — the chip
// itself would be the tell. The AI endpoint answers them; answer() below is
// the deterministic fallback for when it is down.
const SUGGESTIONS = [
  'How many leads do we have?',
  'Which regions have the most leads?',
  'How is the pipeline doing?',
  'What happened recently?',
  'Is Gmail connected?',
]

const NAV_TARGETS = [
  { match: /dashboard/, label: 'Dashboard', to: '/' },
  { match: /chat|assistant/, label: 'Chat', to: '/chat' },
  // One target per vertical in lib/categories.js.
  ...CATEGORIES.map((c) => ({
    match: new RegExp(`${c.key.replace(/-/g, '[ -]?')}|${c.label.toLowerCase()}`),
    label: c.plural,
    to: categoryPath(c.key),
  })),
  { match: /activity/, label: 'Activity', to: '/performance/activity' },
  { match: /sales|revenue|stripe|subscription|mrr/, label: 'Sales', to: '/performance/sales' },
  // Before Events: "error log" and "health log" would otherwise match /log/
  // and open the audit trail instead of the software page.
  { match: /software|uptime|health|error log|heartbeat|latency|incident/, label: 'Software', to: '/performance/software' },
  { match: /event|log/, label: 'Events', to: '/logs/events' },
  { match: /collab|team|member/, label: 'Collaboration', to: '/collaboration' },
  { match: /setting/, label: 'Settings', to: '/settings' },
]

function topRegions(regions, n = 5) {
  return [...(regions || [])]
    .sort((a, b) => b.count - a.count)
    .slice(0, n)
    .map((r, i) => `${i + 1}. ${r.label} — ${r.count.toLocaleString()}`)
    .join('\n')
}

export default function AssistantPanel({ open, onClose }) {
  // Suggestion chips and auto-navigation must never point at a page this
  // role's app does not have — the chip itself would be the tell.
  const { can } = useRbac()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { manifest, mode } = useManifest()
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const bodyRef = useRef(null)
  const panelRef = useRef(null)
  // Set once the server definitively reports no OpenAI key (configured:false)
  // — from then on we answer locally instead of re-asking every message.
  // Transient network failures do NOT set this, so one blip doesn't lock the
  // panel into rigid mode.
  const aiDownRef = useRef(false)

  const counts = useMemo(() => {
    const cats = manifest?.categories ?? {}
    const sum = (arr) => (arr || []).reduce((s, r) => s + (r.count || 0), 0)
    // One entry per vertical, plus the combined total.
    const byCategory = CATEGORIES.map((c) => ({
      ...c,
      count: sum(cats[c.key]),
      regions: cats[c.key] || [],
    }))
    return { byCategory, total: byCategory.reduce((n, c) => n + c.count, 0) }
  }, [manifest])

  // Greet once per open with a data-grounded summary. Only quote the lead
  // count once the manifest has actually loaded — never present 0 as a total.
  useEffect(() => {
    // Re-try the AI endpoint every time the panel opens — the key may have
    // been configured since the last definitive configured:false.
    if (open) aiDownRef.current = false
    if (open && messages.length === 0) {
      const total = counts.total
      setMessages([
        {
          role: 'assistant',
          text: `Hi ${user?.name?.split(' ')[0] || 'there'} — ask me anything. I'm grounded in your live workspace: ${total > 0 ? `${total.toLocaleString()} leads, ` : ''}your pipeline, recent activity, and Gmail.`,
        },
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, busy])

  useEffect(() => {
    if (!open) return
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    // Close on click outside the drawer — but ignore clicks on the Topbar
    // trigger, whose own onClick toggles the panel (otherwise the two would
    // race).
    function onDown(e) {
      if (panelRef.current?.contains(e.target)) return
      if (e.target.closest?.('[data-assistant-launcher]')) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open, onClose])

  async function answer(q) {
    const text = q.toLowerCase()

    // Region-specific count: "how many masonry companies in texas"
    const allRegions = counts.byCategory.flatMap((c) =>
      c.regions.map((r) => ({ ...r, cat: c.key, catLabel: c.label })),
    )
    // The vertical the question names, if any — "how many <label> in texas".
    const catHit = counts.byCategory.find((c) =>
      new RegExp(`${c.key.replace(/-/g, '[ -]?')}|${c.label.toLowerCase()}`).test(text),
    )
    const regionHit = allRegions.filter((r) => text.includes(r.label.toLowerCase().split(' ')[0]) && r.label.length > 3)

    if (/how many|total|count|leads do we/.test(text)) {
      if (catHit && regionHit.length) {
        const rows = regionHit.filter((r) => r.cat === catHit.key)
        if (rows.length) {
          return {
            text: rows
              .map((r) => `${r.label} (${catHit.label}): ${r.count.toLocaleString()} companies`)
              .join('\n'),
          }
        }
      }
      if (catHit) {
        return {
          text: `You have ${catHit.count.toLocaleString()} ${catHit.plural} across ${catHit.regions.length} regions.`,
          actions: [{ label: `Open ${catHit.plural}`, to: categoryPath(catHit.key) }],
        }
      }
      // The per-vertical split only says something once there are two.
      const split =
        counts.byCategory.length > 1
          ? ' — ' +
            counts.byCategory.map((c) => `${c.count.toLocaleString()} ${c.plural}`).join(' and ')
          : ''
      const regionCount = counts.byCategory[0]?.regions.length ?? 0
      return {
        text: `Total: ${counts.total.toLocaleString()} companies${split} across ${regionCount} regions.`,
        actions: counts.byCategory.map((c) => ({ label: c.plural, to: categoryPath(c.key) })),
      }
    }

    if (/top|biggest|largest|best region/.test(text)) {
      const cat = catHit || counts.byCategory[0]
      if (!cat) return { text: 'No leads loaded yet.' }
      return {
        text: `Top ${cat.label} regions:\n${topRegions(cat.regions)}`,
        actions: [{ label: `Open ${cat.plural}`, to: categoryPath(cat.key) }],
      }
    }

    if (/recent|happened|activity|events?\b/.test(text)) {
      const { events } = await listEvents({ limit: 5 })
      if (!events.length) {
        return { text: 'No activity recorded yet — sign-ins, enrichment, drafts, and sends show up as they happen.' }
      }
      return {
        text: events
          .map((e) => `• ${EVENT_TYPES[e.type]?.label || e.type}${e.detail ? ` — ${e.detail}` : ''} (${relativeTime(e.createdAt)})`)
          .join('\n'),
        actions: [{ label: 'Open Events', to: '/logs/events' }],
      }
    }

    // The pipeline, from the intel store — stages a human logged on the lead
    // detail page. These are the only outreach numbers the workspace keeps, and
    // nothing here is measured per-send: a count moves only when someone
    // records a reply, a meeting or a close.
    if (/pipeline|funnel|outreach|deal/.test(text)) {
      const rows = listIntel()
      if (!rows.length) {
        return {
          text: 'Nothing in the pipeline yet — a lead enters it the first time you open its detail page, and moves along as you log replies, meetings and closes.',
          actions: counts.byCategory.map((c) => ({ label: c.plural, to: categoryPath(c.key) })),
        }
      }
      const stage = (s) => rows.filter((r) => r.stage === s).length
      const replied = stage('replied') + stage('meeting') + stage('won')
      const booked = stage('meeting') + stage('won')
      return {
        text: `${rows.length.toLocaleString()} accounts tracked: ${replied} replied, ${booked} booked a meeting, ${stage('won')} won, ${stage('lost')} lost.`,
        actions: [{ label: 'Open Activity', to: '/performance/activity' }],
      }
    }

    if (/gmail|inbox|mail|connect/.test(text)) {
      const s = await gmailStatus()
      if (s.connected) {
        return {
          text: `Gmail is connected as ${s.address} — the app reads replies from it. Email is sent through Apollo, from the Apollo mailbox the send dialog names.`,
          actions: [{ label: 'Open Settings', to: '/settings' }],
        }
      }
      if (s.configured) {
        return { text: 'Gmail isn\'t connected yet — connect it from Settings so the app can read replies from your inbox. Sending goes through Apollo.', actions: [{ label: 'Open Settings', to: '/settings' }] }
      }
      return {
        text: s.available
          ? 'Gmail OAuth isn\'t configured for this deployment yet — an admin adds the Google OAuth client via Terraform, then you can connect in Settings.'
          : 'The Gmail API isn\'t reachable from this environment.',
        actions: [{ label: 'Open Settings', to: '/settings' }],
      }
    }

    const nav = NAV_TARGETS.find((t) => /open|go to|show|take me/.test(text) && t.match.test(text))
    if (nav) {
      return { text: `Taking you to ${nav.label}.`, actions: [{ label: `Open ${nav.label}`, to: nav.to }], go: nav.to }
    }

    return {
      text: 'I can answer from your workspace data — try one of these:',
      suggestions: true,
    }
  }

  // Snapshot of live workspace facts the AI must ground its answer in. Each
  // source degrades independently — a failing fetch thins the grounding, it
  // never blocks the question.
  async function buildContext(question = '') {
    const top = (rs) =>
      [...(rs || [])]
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map((r) => ({ region: r.label, companies: r.count }))
    const ctx = {
      today: new Date().toDateString(),
      user: user?.name || user?.email || '',
      leads: {
        total: counts.total,
        by_category: Object.fromEntries(
          counts.byCategory.map((c) => [c.key, { companies: c.count, regions: c.regions.length }]),
        ),
      },
      top_regions: Object.fromEntries(
        counts.byCategory.map((c) => [c.key, top(c.regions)]),
      ),
      pages: NAV_TARGETS.map((t) => ({ label: t.label, to: t.to })),
    }
    try {
      const rows = listIntel()
      const stage = (s) => rows.filter((r) => r.stage === s).length
      ctx.pipeline = {
        replied: stage('replied') + stage('meeting') + stage('won'),
        meetings_booked: stage('meeting') + stage('won'),
        won: stage('won'),
        lost: stage('lost'),
      }
    } catch {
      /* pipeline unavailable */
    }
    // What the product can DO now — so "how do I…" gets a correct, current
    // answer instead of a guess from the model's priors. Shared with the full
    // chat page (lib/workspaceContext.js) so the drawer and /chat can never
    // describe the product differently.
    ctx.capabilities = CAPABILITIES
    const [ev, gm, teamRes, retrieved] = await Promise.allSettled([
      listEvents({ limit: 6 }),
      gmailStatus(),
      listMembers(),
      // The leads this question is about — the same retrieval the full chat
      // page runs (workspaceContext.js#retrieveLeadContext), so the drawer
      // answers "who's X at Y" from the directory too.
      retrieveLeadContext(question, { manifest, mode }),
    ])
    if (retrieved.status === 'fulfilled' && retrieved.value && typeof retrieved.value === 'object') {
      Object.assign(ctx, retrieved.value)
    }
    if (teamRes.status === 'fulfilled') {
      ctx.team = (teamRes.value.members || []).map((m) => ({ name: m.name, email: m.email, role: m.role }))
    }
    if (ev.status === 'fulfilled' && ev.value.events?.length) {
      ctx.recent_events = ev.value.events.map(
        (e) =>
          `${EVENT_TYPES[e.type]?.label || e.type}${e.detail ? ` — ${e.detail}` : ''} (${relativeTime(e.createdAt)})`,
      )
    }
    if (gm.status === 'fulfilled') {
      ctx.gmail = {
        connected: !!gm.value.connected,
        address: gm.value.address || '',
        configured: !!gm.value.configured,
      }
    }
    return ctx
  }

  async function sendFlow(q) {
    if (busy) return
    const history = [...messages, { role: 'user', text: q }]
      .filter((m) => m.text)
      .slice(-12)
      .map((m) => ({ role: m.role, text: m.text }))
    setMessages((m) => [...m, { role: 'user', text: q }])
    setBusy(true)
    let res = null
    if (!aiDownRef.current) {
      try {
        const r = await gmailAssistant({ messages: history, context: await buildContext(q) })
        if (r.configured && r.text) {
          res = { text: r.text, actions: (r.nav || []).map((n) => ({ label: n.label, to: n.to })) }
        } else {
          aiDownRef.current = true
        }
      } catch {
        /* endpoint unreachable — answer locally this time */
      }
    }
    if (!res) res = await answer(q)
    setBusy(false)
    setMessages((m) => [...m, { role: 'assistant', ...res }])
    if (res.go && pathAllowedFor(can, res.go)) {
      navigate(res.go)
      onClose()
    }
  }

  function handleSend(e) {
    e?.preventDefault()
    const q = input.trim()
    if (!q || busy) return
    setInput('')
    sendFlow(q)
  }

  function ask(q) {
    setInput('')
    sendFlow(q)
  }

  return (
    <aside
      ref={panelRef}
      className={`fixed inset-y-0 right-0 z-[42] flex w-[min(400px,calc(100vw-48px))] flex-col overflow-hidden bg-surface [border-left:1px_solid_var(--border)] transition-transform duration-200 ease-[cubic-bezier(0.32,0.72,0.24,1)] ${open ? 'translate-x-0 shadow-[-24px_0_60px_-24px_rgba(2,6,23,0.3)]' : 'pointer-events-none translate-x-full'}`}
      aria-hidden={!open}
    >
        <div className="flex items-center gap-x-[10px] [border-bottom:1px_solid_var(--border)] px-[24px] py-6">
          <div>
            <div className="font-sora text-[14px] font-semibold text-ink">Assistant</div>
            <div className="font-sora text-[11.5px] text-dim">Ask anything — grounded in your workspace data</div>
          </div>
          <button
            className="ml-auto grid h-[30px] w-[30px] place-items-center rounded-[8px] bg-transparent text-dim transition-colors hover:bg-panel hover:text-ink [&_svg]:h-[18px] [&_svg]:w-[18px]"
            onClick={onClose}
            aria-label="Close assistant"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-y-[10px] overflow-y-auto p-4" ref={bodyRef}>
          {messages.map((m, i) => (
            <div
              key={i}
              className={`max-w-[88%] whitespace-pre-wrap rounded-[12px] px-3 py-[9px] text-[13px] leading-[1.5] ${
                m.role === 'user'
                  ? 'self-end rounded-br-[4px] bg-primary text-white'
                  : 'self-start rounded-bl-[4px] border border-line bg-panel text-ink'
              }`}
            >
              {m.text}
              {(m.actions || m.suggestions) && (
                <div className="mt-2 flex flex-wrap gap-[6px]">
                  {m.actions?.filter((a) => pathAllowedFor(can, a.to)).map((a) => (
                    <button
                      key={a.to}
                      className="inline-flex items-center gap-x-[7px] rounded-full border border-line bg-surface px-3 py-[7px] text-[13px] font-semibold text-dim transition-colors hover:border-faint"
                      onClick={() => {
                        navigate(a.to)
                        onClose()
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                  {m.suggestions &&
                    SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        className="inline-flex items-center gap-x-[7px] rounded-full border border-line bg-surface px-3 py-[7px] text-[13px] font-semibold text-dim transition-colors hover:border-faint"
                        onClick={() => ask(s)}
                      >
                        {s}
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="inline-flex items-center gap-x-[5px] self-start rounded-[12px] rounded-bl-[4px] border border-line bg-panel px-3 py-[11px]">
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className="h-[6px] w-[6px] animate-bounce rounded-full bg-dim"
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-[6px] px-4 pb-[10px]">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                className="inline-flex items-center gap-x-[7px] rounded-full border border-line bg-surface px-3 py-[7px] text-[13px] font-semibold text-dim transition-colors hover:border-faint"
                onClick={() => ask(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form className="flex gap-x-2 [border-top:1px_solid_var(--border)] px-4 py-3" onSubmit={handleSend}>
          <input
            className="flex-1 rounded-[10px] border border-line bg-surface px-3 py-[9px] text-[13px] text-ink focus:border-primary focus:outline-none"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about leads, regions, pipeline, activity…"
            aria-label="Ask the assistant"
          />
          <button
            className="inline-flex items-center justify-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
            type="submit"
            disabled={busy}
            aria-label="Send"
          >
            <SendIcon className="h-4 w-4" />
          </button>
        </form>
    </aside>
  )
}
